import classNames from "classnames";
import { useFilterManager, FilterManagerWrapper } from "@ugg/shared/components/FilterManager/FilterManager";
import { PatchOption } from "@ugg/shared/interfaces/filter-manager.interface";
import { validParams } from "@ugg/shared/query-params/valid-params";
import { getValidParamKeys } from "@ugg/shared/query-params/params-helpers";
import { MediaQuery } from "@outplayed/responsive";
import { ReactComponent as FilterIcon } from "@ugg/shared/assets/svg/filters-icon.svg";
import { TIER_LIST_PAGES, useTierListPage } from "@ugg/shared/pages/tier-list-pages";
import { TIER_LIST_ROLE_OPTIONS } from "@ugg/shared/query-params/filter-options/tier-lists/tier-list";

import { usePatchOptions } from "@ugg/shared/components/FilterManager/filters/usePatchOptions";
import Role from "@ugg/shared/components/FilterManager/filters/Role";
import RoleSplit from "@ugg/shared/components/FilterManager/filters/RoleSplit";
import TierListRole from "@ugg/shared/components/FilterManager/filters/TierListRole";
import TierListRoleSplit from "@ugg/shared/components/FilterManager/filters/TierListRoleSplit";
import Rank from "@ugg/shared/components/FilterManager/filters/Rank";
import QueueType from "@ugg/shared/components/FilterManager/filters/QueueType";
import Region from "@ugg/shared/components/FilterManager/filters/Region";
import Patch from "@ugg/shared/components/FilterManager/filters/Patch";
import DuoRole from "@ugg/shared/components/FilterManager/filters/DuoRole";
import ShowAll from "@ugg/shared/components/FilterManager/filters/ShowAll";
import { QueueTypeS } from "@ugg/shared/utils/queue-type-helpers";

interface TierListFilterManagerProps {
  className?: string;
  // Optional content rendered as the LAST item of the filters group —
  // e.g. a page-specific search box. It lives INSIDE the same flex-wrap
  // group as the filter chips (not as a separate sibling), so it wraps
  // onto a fitting row — or its own row — right alongside them instead of
  // floating on the container's single row. Backward-compatible: when
  // omitted, the bar renders exactly as before, so pages that don't opt in
  // (all of them except Classic today) are unaffected. Reused by future
  // surfaces that need an inline filter-bar control (e.g. ARAM Mayhem
  // augments).
  endSlot?: React.ReactNode;
}

export const TierListFilterManager: React.FC<TierListFilterManagerProps> = ({ className, endSlot }) => {
  const page = useTierListPage();
  const filterManagerRes = useFilterManager(page, validParams);
  const { filters } = filterManagerRes;

  const validParamKeys = getValidParamKeys(validParams, page);
  const options = validParams[page];
  const patchFilterOption = ("patch" in options ? options.patch : { options: "latest", max: 5 }) as PatchOption;
  const patchOptions = usePatchOptions(patchFilterOption, filters.patch, options.default.patch);

  const isMainTierList = [
    TIER_LIST_PAGES.DEFAULT_TIER_LIST,
    TIER_LIST_PAGES.TOP_TIER_LIST,
    TIER_LIST_PAGES.JUNGLE_TIER_LIST,
    TIER_LIST_PAGES.MID_TIER_LIST,
    TIER_LIST_PAGES.ADC_TIER_LIST,
    TIER_LIST_PAGES.SUPPORT_TIER_LIST,
  ].some((el) => el === page);

  const hasRank = [QueueTypeS.RANKED_SOLO, QueueTypeS.RANKED_FLEX].includes(filters.queueType);
  const hasRole = [
    QueueTypeS.RANKED_SOLO,
    QueueTypeS.RANKED_FLEX,
    QueueTypeS.NORMAL_DRAFT,
    QueueTypeS.QUICKPLAY,
    QueueTypeS.SWIFTPLAY,
    QueueTypeS.NEXUS_BLITZ,
    QueueTypeS.CLASSIC,
  ].includes(filters.queueType);

  // Temp classname. Fix later
  return (
    <FilterManagerWrapper className={classNames("filter-manager_tier-list filter-manager", className)}>
      <div className="filter-manager_label">
        <FilterIcon />
        <MediaQuery min="TABLET" max="DESKTOP_LARGE">
          <span>Filters</span>
        </MediaQuery>
      </div>
      <div className="flex-1 flex items-center flex-wrap gap-[12px]">
        {hasRole && !isMainTierList && validParamKeys.role && (
          <>
            <MediaQuery min="MOBILE_SMALL" max="TABLET">
              <Role {...filterManagerRes} options={options.role ?? []} />
            </MediaQuery>
            <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE">
              <RoleSplit {...filterManagerRes} options={options.role ?? []} />
            </MediaQuery>
          </>
        )}
        {hasRole && isMainTierList && (
          <>
            <MediaQuery min="MOBILE_SMALL" max="TABLET">
              <TierListRole {...filterManagerRes} page={page as TIER_LIST_PAGES} defaultOption={TIER_LIST_ROLE_OPTIONS[0]} />
            </MediaQuery>
            <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE">
              <TierListRoleSplit {...filterManagerRes} defaultOption={TIER_LIST_ROLE_OPTIONS[0]} />
            </MediaQuery>
          </>
        )}
        {validParamKeys.duo && <DuoRole {...filterManagerRes} options={options.duo ?? []} />}
        {validParamKeys.rank && hasRank && <Rank {...filterManagerRes} options={options.rank ?? []} />}
        {validParamKeys.queueType && (
          <QueueType {...filterManagerRes} options={"queueType" in options ? options.queueType : []} />
        )}
        {validParamKeys.patch && (
          <Patch {...filterManagerRes} options={patchOptions.options} isDefaultPatch={patchOptions.isDefaultPatch} />
        )}
        {validParamKeys.region && <Region {...filterManagerRes} options={options.region ?? []} />}
        {validParamKeys.allChampions && <ShowAll {...filterManagerRes} />}
        {/* Page-specific slot as the last chip in the wrap group, so it
            flows/wraps onto a fitting row (or its own row) alongside the
            filters rather than floating on the container's row. */}
        {endSlot && <div className="shrink-0">{endSlot}</div>}
      </div>
    </FilterManagerWrapper>
  );
};
